import type { PlayerSnapshot, SnapshotMessage, SplashImpact, SplashShot, TowerSnapshot, TowerType, UnitSnapshot } from "@towerhold/protocol";
import { CANONICAL, playerColorForSeat } from "./art/palettes.js";
import * as debugOverlay from "./debugOverlay.js";
import { drawTowerFlag, drawUnitFlag, TOWER_DRAWS, UNIT_DRAWS } from "./art/silhouettes.js";

// ---------------------------------------------------------------------------
// Projectile system
// ---------------------------------------------------------------------------

const PROJECTILE_DURATION_S = 0.15;
/** Server tick rate is 10 Hz; one tick == 100 ms. Used to convert tick deltas
 *  in splash_shot events into wall-clock animation duration. */
const TICK_MS = 100;
/** How long the impact ring + filled disc stay on screen after a splash lands. */
const FLASH_DURATION_MS = 250;

// ---------------------------------------------------------------------------
// Smooth reconciliation (jitter masking)
// ---------------------------------------------------------------------------
//
// Snapshots arrive from the server at ~100ms cadence on the wire, but with
// network jitter (WiFi + ISP variance) the browser can dispatch them with
// gaps up to 300ms+. The client extrapolates unit positions forward via
// `unit.x + unit.vx * dt` between snapshots, so when a late snapshot arrives
// the predicted position has overshot reality and the unit visibly snaps
// back. That snap is the perceived "teleport".
//
// Fix: instead of snapping at snapshot arrival, drift the rendered position
// toward truth over ~100ms. We maintain a per-unit offset that captures the
// snap delta at arrival; the renderer adds it to the ideal position and
// decays it exponentially each frame. After ~3 half-lives (~300ms) the
// offset is sub-pixel — invisible. Standard netcode pattern (Source/Quake).
const SMOOTH_HALF_LIFE_S = 0.1;       // 100ms — matches one server tick
/** If a single snap exceeds this magnitude we don't smooth — snapping
 *  instantly is visually less weird than a multi-cell glide. Reconnects,
 *  teleports, and major server corrections fall through this. */
const SMOOTH_MAX_OFFSET_CELLS = 1.5;

type Projectile = {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  towerType: TowerType;
  progress: number; // 0..1
};

/** Mortar shell from a cannon/mortar. Arcs from origin to impact center over
 *  a wall-clock duration matching the server's flightTimeTicks. */
type SplashProjectile = {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  towerType: TowerType;
  /** Splash radius in world pixels (for debug overlay during flight). */
  radiusPx: number;
  startTime: number;
  durationMs: number;
};

type SplashFlash = {
  worldX: number;
  worldY: number;
  radiusPx: number;
  towerType: TowerType;
  startTime: number;
};

// ---------------------------------------------------------------------------
// Lane / grid constants (must match server domain/lane.ts)
// ---------------------------------------------------------------------------

const LANE_COLS  = 7;
const LANE_ROWS  = 18;
const GUTTER_COLS = 2;

const SPAWN_ROW_MAX = 2;
const BUILD_ROW_MIN = SPAWN_ROW_MAX + 1;
const BUILD_ROW_MAX = 15;

// Lane Expansion (T3 B1) appends 3 rows to the back of a player's build zone
// per level, pushing exit + leak rows deeper. Per-player; mirror of
// apps/server/src/domain/lane.ts laneGeometry().
function laneRowsForLevel(level: number): number {
  return LANE_ROWS + 3 * level;
}
function buildRowMaxForLevel(level: number): number {
  return BUILD_ROW_MAX + 3 * level;
}

/** Canonical world-pixel size of one grid cell. All world geometry uses this. */
const BASE_CELL_SIZE = 40;

/** Camera zoom bounds. */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3.0;
const DEFAULT_ZOOM = 1.0;

const COLORS = {
  gutter:      "#1a3a08",
  gridLine:    "rgba(0,0,0,0.12)",
  zoneBorder:  "rgba(0,0,0,0.22)",
  laneEdge:    "rgba(0,0,0,0.28)",
  noise:       "rgba(0,0,0,0.08)",
  motifLight:  "rgba(255,255,180,0.18)",
  motifDot:    "rgba(0,0,0,0.22)",
  motifDanger: "rgba(255,60,20,0.28)",
};

const EXIT_PULSE_COLOR = { r: 255, g: 60, b: 50 };
const EXIT_PULSE_HZ = 1.5;

/** Tower colors progress on one hue so the upgrade chain reads as a single family. */
const TOWER_COLORS: Record<TowerType, string> = {
  arrow:    "#f59e0b", // amber
  ballista: "#f97316", // orange
  cannon:   "#dc2626", // red — splash
  arbalest: "#ea580c", // deeper orange
  harpoon:  "#0ea5e9", // cyan — air
  mortar:  "#7f1d1d", // deep crimson — heavy splash
  polybolos: "#c2410c", // burnt sienna — T3 generalist
  skyhook:   "#0369a1", // deep ocean — T3 AA
  howitzer:  "#991b1b", // dark crimson — T3 splash
  skorpion:  "#9a3412", // chestnut — T4 generalist
  stormpike: "#075985", // ocean trench — T4 AA
  culverin:  "#7f1d1d", // dark blood — T4 splash
  manticore:  "#facc15", // gold — T5 generalist
  skybreaker: "#0ea5e9", // bright sky — T5 AA
  bombard:    "#dc2626", // bright crimson — T5 splash
};

const ZONE_COLORS: Record<string, string> = {
  spawn: "#4a8c20",
  build: "#6b4a18",
  exit:  "#7a1a08",
};

// ---------------------------------------------------------------------------
// Draw helpers — all geometry in world units (BASE_CELL_SIZE per cell).
// Camera transform is applied once per frame; helpers draw in world space.
// ---------------------------------------------------------------------------

function laneX(laneIndex: number): number {
  return laneIndex * (LANE_COLS + GUTTER_COLS) * BASE_CELL_SIZE;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function paintLaneNoise(ctx: CanvasRenderingContext2D, ox: number, laneRows: number = LANE_ROWS): void {
  const cs = BASE_CELL_SIZE;
  ctx.fillStyle = COLORS.noise;
  for (let row = 0; row < laneRows; row++) {
    for (let col = 0; col < LANE_COLS; col++) {
      const rand = mulberry32(row * 1009 + col * 31 + 7);
      const dots = 2 + Math.floor(rand() * 3); // 2..4
      for (let i = 0; i < dots; i++) {
        const px = ox + col * cs + Math.floor(rand() * cs);
        const py = row * cs + Math.floor(rand() * cs);
        ctx.fillRect(px, py, 1, 1);
      }
    }
  }
}

function paintChevron(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  const cs = BASE_CELL_SIZE;
  const w = cs * 0.55;
  const h = cs * 0.3;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, Math.round(cs * 0.05));
  ctx.beginPath();
  ctx.moveTo(cx - w / 2, cy - h / 2);
  ctx.lineTo(cx,         cy + h / 2);
  ctx.lineTo(cx + w / 2, cy - h / 2);
  ctx.stroke();
}

function paintLaneMotifs(ctx: CanvasRenderingContext2D, ox: number, level: number = 0): void {
  const cs = BASE_CELL_SIZE;
  const laneRows = laneRowsForLevel(level);
  const buildMax = buildRowMaxForLevel(level);
  for (let row = 0; row < laneRows; row++) {
    for (let col = 0; col < LANE_COLS; col++) {
      const cx = ox + (col + 0.5) * cs;
      const cy = (row + 0.5) * cs;
      if (row <= SPAWN_ROW_MAX) {
        paintChevron(ctx, cx, cy, COLORS.motifLight);
      } else if (row <= buildMax) {
        ctx.fillStyle = COLORS.motifDot;
        const r = Math.max(1, Math.round(cs * 0.05));
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      } else {
        paintChevron(ctx, cx, cy, COLORS.motifDanger);
      }
    }
  }
}

function paintLaneBackground(
  ctx: CanvasRenderingContext2D,
  laneIndex: number,
  level: number = 0,
): void {
  const cs = BASE_CELL_SIZE;
  const ox = laneX(laneIndex);
  const laneRows = laneRowsForLevel(level);
  const buildMax = buildRowMaxForLevel(level);

  for (let row = 0; row < laneRows; row++) {
    ctx.fillStyle =
      row <= SPAWN_ROW_MAX ? ZONE_COLORS.spawn :
      row <= buildMax      ? ZONE_COLORS.build :
                             ZONE_COLORS.exit;
    ctx.fillRect(ox, row * cs, LANE_COLS * cs, cs);
  }

  paintLaneNoise(ctx, ox, laneRows);
  paintLaneMotifs(ctx, ox, level);

  ctx.strokeStyle = COLORS.zoneBorder;
  ctx.lineWidth = 1.5;
  for (const divRow of [SPAWN_ROW_MAX + 1, buildMax + 1]) {
    ctx.beginPath();
    ctx.moveTo(ox, divRow * cs);
    ctx.lineTo(ox + LANE_COLS * cs, divRow * cs);
    ctx.stroke();
  }

  ctx.strokeStyle = COLORS.laneEdge;
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(ox + 0.5, 0); ctx.lineTo(ox + 0.5, laneRows * cs); ctx.stroke();
  const rx = ox + LANE_COLS * cs - 0.5;
  ctx.beginPath(); ctx.moveTo(rx, 0); ctx.lineTo(rx, laneRows * cs); ctx.stroke();
}

function buildBackgroundTexture(playerCount: number, levels: number[]): HTMLCanvasElement {
  const totalCols = playerCount * LANE_COLS + (playerCount - 1) * GUTTER_COLS;
  const width  = totalCols * BASE_CELL_SIZE;
  // Texture height fits the tallest lane; shorter lanes paint over the gutter
  // colour (drawn first). This keeps the texture single-buffer-friendly.
  const maxRows = Math.max(LANE_ROWS, ...levels.map(laneRowsForLevel));
  const height = maxRows * BASE_CELL_SIZE;
  const off = document.createElement("canvas");
  off.width = width;
  off.height = height;
  const octx = off.getContext("2d")!;
  octx.fillStyle = COLORS.gutter;
  octx.fillRect(0, 0, width, height);
  for (let i = 0; i < playerCount; i++) paintLaneBackground(octx, i, levels[i] ?? 0);
  return off;
}

function drawExitPulse(
  ctx: CanvasRenderingContext2D,
  laneIndex: number,
  player: PlayerSnapshot | undefined,
  timeMs: number,
): void {
  const cs = BASE_CELL_SIZE;
  let maxY = 0;
  if (player !== undefined) {
    for (const unit of player.units) {
      if (unit.y > maxY) maxY = unit.y;
    }
  }
  const level = player?.laneExpansionLevel ?? 0;
  const leakRow = laneRowsForLevel(level) - 1;
  const proximity = Math.max(0, Math.min(1, (maxY - (leakRow - 3)) / 3));

  const sine = 0.5 + 0.5 * Math.sin((timeMs / 1000) * EXIT_PULSE_HZ * 2 * Math.PI);
  const alpha = (0.06 + 0.16 * proximity) * (0.6 + 0.4 * sine);

  const ox = laneX(laneIndex);
  const { r, g, b } = EXIT_PULSE_COLOR;
  ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
  const lineH = Math.max(2, Math.round(cs * 0.08));
  ctx.fillRect(ox, leakRow * cs - lineH / 2, LANE_COLS * cs, lineH);
}

function drawOwnLaneOverlay(ctx: CanvasRenderingContext2D, laneIndex: number, level: number = 0): void {
  const cs = BASE_CELL_SIZE;
  const ox = laneX(laneIndex);
  const laneRows = laneRowsForLevel(level);
  ctx.fillStyle = "rgba(255,220,80,0.13)";
  ctx.fillRect(ox, 0, LANE_COLS * cs, laneRows * cs);

  ctx.strokeStyle = "rgba(255,200,40,0.90)";
  ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(ox + 1.5, 0); ctx.lineTo(ox + 1.5, laneRows * cs); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(ox + LANE_COLS * cs - 1.5, 0); ctx.lineTo(ox + LANE_COLS * cs - 1.5, laneRows * cs); ctx.stroke();
}

/**
 * Tower: silhouette + player-colored base-ring + damage-only HP bar.
 * Tier reads from the silhouette progression (arrow → ballista → arbalest).
 */
function drawTower(
  ctx: CanvasRenderingContext2D,
  tower: TowerSnapshot,
  laneIndex: number,
  seatIndex: number,
): void {
  const cs = BASE_CELL_SIZE;
  const ox = laneX(laneIndex);
  const cx = ox + (tower.cell.col + 0.5) * cs;
  const cy = (tower.cell.row + 0.5) * cs;
  const size = cs;

  const building = tower.buildProgress < 1;
  const selling = tower.sellProgress > 0;

  const palette = CANONICAL[tower.type];
  const draw = TOWER_DRAWS[tower.type];
  if (palette !== undefined && draw !== undefined) {
    if (building || selling) {
      ctx.save();
      ctx.globalAlpha = 0.45;
      draw(ctx, cx, cy, size, palette);
      ctx.restore();
    } else {
      draw(ctx, cx, cy, size, palette);
    }
  }

  // Player flag = ownership cue. Rendered AFTER the body and OUTSIDE the
  // build-fade alpha so other players can read ownership immediately, even
  // while the tower is still materializing.
  drawTowerFlag(ctx, cx, cy, size, tower.type, playerColorForSeat(seatIndex));

  if (building || selling) {
    // Progress arc traced over the base-ring ellipse, sweeping CW from
    // 12 o'clock. Gold during construction; red while selling.
    const progress = selling ? tower.sellProgress : tower.buildProgress;
    const ringCx = cx;
    const ringCy = cy + size * 0.44;
    const rx = size * 0.44;
    const ry = size * 0.12;
    const start = -Math.PI / 2;
    const end = start + Math.PI * 2 * progress;
    const color = selling ? "#ff5555" : "#ffcc33";
    const shadow = selling ? "rgba(255, 85, 85, 0.7)" : "rgba(255, 204, 51, 0.7)";
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, size * 0.05);
    ctx.lineCap = "round";
    ctx.shadowColor = shadow;
    ctx.shadowBlur = size * 0.15;
    ctx.beginPath();
    ctx.ellipse(ringCx, ringCy, rx, ry, 0, start, end);
    ctx.stroke();
    ctx.restore();
  }

  if (!building && tower.hp < tower.maxHp) {
    const ratio = Math.max(0, tower.hp / tower.maxHp);
    const pad = Math.max(2, Math.round(cs * 0.075));
    const barH = 3;
    const barW = cs - pad * 2;
    const barX = ox + tower.cell.col * cs + pad;
    const barY = tower.cell.row * cs + pad - barH - 1;
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(barX, barY, barW, barH);
    ctx.fillStyle = ratio > 0.5 ? "#00cc66" : ratio > 0.25 ? "#ffaa00" : "#ff4444";
    ctx.fillRect(barX, barY, barW * ratio, barH);
  }
}

function drawUnit(
  ctx: CanvasRenderingContext2D,
  unit: UnitSnapshot,
  laneIndex: number,
  posX: number,
  posY: number,
  ownerSeatIndex: number,
): void {
  const cs = BASE_CELL_SIZE;
  const ox = laneX(laneIndex);

  const cx = ox + posX * cs;
  const cy = posY * cs;

  const sizeMult = unit.type === "raider" ? 1.1 : unit.type === "soldier" ? 1.0 : 0.95;
  const size = cs * sizeMult;

  const palette = CANONICAL[unit.type];
  const draw = UNIT_DRAWS[unit.type];
  if (palette !== undefined && draw !== undefined) {
    draw(ctx, cx, cy, size, palette);
  }

  drawUnitFlag(ctx, cx, cy, size, unit.type, playerColorForSeat(ownerSeatIndex));

  const hpRatio = unit.hp / unit.maxHp;
  const barH = 3;
  const barW = size * 0.7;
  const barX = cx - barW / 2;
  const barY = cy - size * 0.55 - barH - 2;
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(barX, barY, barW, barH);
  ctx.fillStyle = hpRatio > 0.5 ? "#00cc66" : hpRatio > 0.25 ? "#ffaa00" : "#ff4444";
  ctx.fillRect(barX, barY, barW * hpRatio, barH);
}

function drawProjectile(ctx: CanvasRenderingContext2D, proj: Projectile): void {
  const x = lerp(proj.startX, proj.endX, proj.progress);
  const y = lerp(proj.startY, proj.endY, proj.progress);
  const color = TOWER_COLORS[proj.towerType] ?? "#ffffff";

  const dx = proj.endX - proj.startX;
  const dy = proj.endY - proj.startY;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const tierScale = proj.towerType === "arbalest" ? 8 : proj.towerType === "ballista" ? 6 : 5;
  const nx = (dx / len) * tierScale;
  const ny = (dy / len) * tierScale;
  ctx.strokeStyle = color;
  ctx.lineWidth = proj.towerType === "arbalest" ? 3 : proj.towerType === "ballista" ? 2.5 : 2;
  ctx.beginPath();
  ctx.moveTo(x - nx, y - ny);
  ctx.lineTo(x + nx, y + ny);
  ctx.stroke();
}

/** Mortar shell — straight-line interpolation in xy, with a parabolic vertical
 *  hop subtracted off so the shell visibly arcs above the line. Renders as a
 *  small filled disc with a faint shadow disc fixed at ground level. */
function drawSplashProjectile(ctx: CanvasRenderingContext2D, p: SplashProjectile, t: number): void {
  const groundX = lerp(p.startX, p.endX, t);
  const groundY = lerp(p.startY, p.endY, t);
  const arcHeight = Math.max(24, p.radiusPx * 0.6);
  const x = groundX;
  const y = groundY - Math.sin(Math.PI * t) * arcHeight;

  const color = TOWER_COLORS[p.towerType] ?? "#ffffff";

  // Faint ground shadow at impact line, shrinks slightly as shell rises.
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(groundX, groundY, 5, 2.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // The shell itself.
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, p.towerType === "mortar" ? 4.5 : 3.5, 0, Math.PI * 2);
  ctx.fill();
}

function drawSplashFlash(ctx: CanvasRenderingContext2D, f: SplashFlash, t: number): void {
  // t ∈ [0..1] over FLASH_DURATION_MS. Ring expands from 60% → 100%; alpha fades.
  const color = TOWER_COLORS[f.towerType] ?? "#ffffff";
  const ringR = f.radiusPx * (0.6 + 0.4 * t);
  const ringAlpha = 0.85 * (1 - t);
  const fillAlpha = 0.35 * (1 - t);

  // Translucent fill that fades — sells the area-of-effect feel.
  ctx.fillStyle = colorWithAlpha(color, fillAlpha);
  ctx.beginPath();
  ctx.arc(f.worldX, f.worldY, f.radiusPx, 0, Math.PI * 2);
  ctx.fill();

  // Expanding ring outline.
  ctx.strokeStyle = colorWithAlpha(color, ringAlpha);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(f.worldX, f.worldY, ringR, 0, Math.PI * 2);
  ctx.stroke();
}

/** Dashed circle showing splash radius — debug overlay only. */
function drawSplashRadiusOverlay(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string): void {
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = colorWithAlpha(color, 0.7);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function colorWithAlpha(hex: string, alpha: number): string {
  // Accept #rrggbb only — that's all TOWER_COLORS uses.
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function drawHoverPreview(
  ctx: CanvasRenderingContext2D,
  cell: CellCoord,
  ownPlayer: PlayerSnapshot | null,
): void {
  const buildMax = buildRowMaxForLevel(ownPlayer?.laneExpansionLevel ?? 0);
  if (cell.row < BUILD_ROW_MIN || cell.row > buildMax) return;
  const cs = BASE_CELL_SIZE;

  const ox = laneX(cell.laneIndex);
  const x  = ox + cell.col * cs;
  const y  = cell.row * cs;

  const isOccupied = ownPlayer?.towers.some(
    (t) => t.cell.col === cell.col && t.cell.row === cell.row,
  ) ?? false;

  // Mirror of UNIT_RADIUS + `flies` in apps/server/src/domain/units.ts — a
  // unit's collision envelope overlapping the cell blocks the build. Flyers
  // pass over. If a new flyer type is added, add it to FLYER_TYPES below.
  const UNIT_RADIUS_CLIENT = 0.3;
  const FLYER_TYPES = new Set(["skyLancer"]);
  const isUnitHere = ownPlayer?.units.some(
    (u) => !FLYER_TYPES.has(u.type)
        && u.x + UNIT_RADIUS_CLIENT > cell.col
        && u.x - UNIT_RADIUS_CLIENT < cell.col + 1
        && u.y + UNIT_RADIUS_CLIENT > cell.row
        && u.y - UNIT_RADIUS_CLIENT < cell.row + 1,
  ) ?? false;

  if (isOccupied || isUnitHere) {
    ctx.fillStyle   = "rgba(147,197,253,0.22)";
    ctx.strokeStyle = "rgba(147,197,253,0.6)";
  } else {
    ctx.fillStyle   = "rgba(255,255,255,0.12)";
    ctx.strokeStyle = "rgba(255,255,255,0.45)";
  }

  ctx.fillRect(x, y, cs, cs);
  ctx.setLineDash([3, 3]);
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, cs, cs);
  ctx.setLineDash([]);
}

// ---------------------------------------------------------------------------
// Public renderer
// ---------------------------------------------------------------------------

export type CellCoord = { laneIndex: number; col: number; row: number };

export interface Renderer {
  applySnapshot(snapshot: SnapshotMessage): void;
  setHoverCell(cell: CellCoord | null): void;
  setSelectedCells(cells: CellCoord[]): void;
  resize(): void;

  /** Convert a viewport-client coord (e.g. MouseEvent.clientX/Y) to a grid cell, or null if off-world. */
  cellFromClient(clientX: number, clientY: number): CellCoord | null;

  /** Convert a grid cell to its viewport-client center (inverse of cellFromClient). */
  clientFromCell(laneIndex: number, col: number, row: number): { x: number; y: number };

  /** Drag-pan lifecycle. */
  beginPan(clientX: number, clientY: number): void;
  updatePan(clientX: number, clientY: number): void;
  endPan(): void;

  /** Pan by a client-pixel delta (keyboard/buttons). Internally divides by zoom. */
  panByClientPixels(dxCss: number, dyCss: number): void;

  /** Multiply zoom, keeping the world point under (clientX,clientY) fixed. */
  zoomAroundClient(clientX: number, clientY: number, factor: number): void;

  /** Multiply zoom around the canvas center. */
  zoomAroundCenter(factor: number): void;

  /** Center on a given lane at default zoom. */
  focusOnLane(laneIndex: number): void;

  /** Toggle the splash-radius debug overlay. Returns the new state. */
  toggleSplashRadii(): boolean;

  destroy(): void;
}

export function createRenderer(
  canvas: HTMLCanvasElement,
  container: HTMLElement,
  playerCount: number,
  ownLaneIndex: number,
): Renderer {
  const totalLaneCols = playerCount * LANE_COLS + (playerCount - 1) * GUTTER_COLS;
  const worldW = totalLaneCols * BASE_CELL_SIZE;
  // Per-player Lane Expansion levels (mirrored from snapshot). Drives bgTexture
  // height + per-lane build/leak rows. Updated in applySnapshot.
  let laneLevels: number[] = new Array(playerCount).fill(0);
  function maxLaneRows(): number {
    return Math.max(LANE_ROWS, ...laneLevels.map(laneRowsForLevel));
  }
  let worldH = maxLaneRows() * BASE_CELL_SIZE;

  let dpr = window.devicePixelRatio || 1;

  function applyCanvasSize(): void {
    dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, container.clientWidth);
    const h = Math.max(1, container.clientHeight);
    canvas.style.width  = w + "px";
    canvas.style.height = h + "px";
    canvas.width  = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
  }

  applyCanvasSize();

  const ctx = canvas.getContext("2d")!;
  const camera = {
    x: worldW / 2,
    y: worldH / 2,
    zoom: DEFAULT_ZOOM,
  };
  let panDrag: { startCssX: number; startCssY: number; startCamX: number; startCamY: number } | null = null;

  let lastSnapshot: SnapshotMessage | null = null;
  let lastSnapshotTime = 0;
  /** Per-unit reconciliation offset. On snapshot arrival, the snap delta
   *  (predicted - actual position) is added to the unit's offset so the
   *  rendered position is unchanged at the moment of arrival. Each frame
   *  the offset decays exponentially toward zero, smoothly converging the
   *  rendered position onto the new snapshot's truth. See SMOOTH_HALF_LIFE_S. */
  const unitSmoothing = new Map<string, { offsetX: number; offsetY: number }>();
  let hoverCell: CellCoord | null = null;
  let selectedCells: CellCoord[] = [];
  let rafId = 0;
  let lastFrameTime = 0;
  const projectiles: Projectile[] = [];
  const splashProjectiles: SplashProjectile[] = [];
  const splashFlashes: SplashFlash[] = [];
  let showSplashRadii = false;

  let bgTexture = buildBackgroundTexture(playerCount, laneLevels);

  // -----------------------------------------------------------------------
  // Camera math
  // -----------------------------------------------------------------------

  function clampCamera(): void {
    camera.zoom = clamp(camera.zoom, MIN_ZOOM, MAX_ZOOM);
    // Allow camera to drift up to a quarter-world past each edge so you can
    // comfortably frame corner lanes without jarring clamps.
    camera.x = clamp(camera.x, -worldW * 0.25, worldW * 1.25);
    camera.y = clamp(camera.y, -worldH * 0.25, worldH * 1.25);
  }

  /** Client (viewport) coords → world coords. */
  function clientToWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const cssX = clientX - rect.left;
    const cssY = clientY - rect.top;
    return {
      x: (cssX - rect.width  / 2) / camera.zoom + camera.x,
      y: (cssY - rect.height / 2) / camera.zoom + camera.y,
    };
  }

  function cellFromWorld(wx: number, wy: number): CellCoord | null {
    const cs = BASE_CELL_SIZE;
    const laneStride = (LANE_COLS + GUTTER_COLS) * cs;
    const laneIndex  = Math.floor(wx / laneStride);
    if (laneIndex < 0 || laneIndex >= playerCount) return null;
    const xInLane = wx - laneIndex * laneStride;
    if (xInLane < 0 || xInLane >= LANE_COLS * cs) return null;
    const col = Math.floor(xInLane / cs);
    const row = Math.floor(wy / cs);
    const laneRows = laneRowsForLevel(laneLevels[laneIndex] ?? 0);
    if (row < 0 || row >= laneRows) return null;
    return { laneIndex, col, row };
  }

  // -----------------------------------------------------------------------
  // Frame loop
  // -----------------------------------------------------------------------

  function frame(now: DOMHighResTimeStamp): void {
    const frameStart = performance.now();
    const dt = lastFrameTime === 0 ? 0 : (now - lastFrameTime) / 1000;
    lastFrameTime = now;
    rafId = requestAnimationFrame(frame);

    // Background fill covers the area outside the world (exposed when panned/zoomed).
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = COLORS.gutter;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Camera transform: world → canvas pixels (includes DPR).
    const s = camera.zoom * dpr;
    ctx.setTransform(
      s, 0, 0, s,
      canvas.width  / 2 - camera.x * s,
      canvas.height / 2 - camera.y * s,
    );

    ctx.drawImage(bgTexture, 0, 0);

    if (lastSnapshot === null) return;

    const dtSinceSnapshot = Math.min(0.25, lastSnapshotTime === 0 ? 0 : (now - lastSnapshotTime) / 1000);
    const { players } = lastSnapshot;

    drawOwnLaneOverlay(ctx, ownLaneIndex, laneLevels[ownLaneIndex] ?? 0);

    for (let i = 0; i < players.length; i++) {
      drawExitPulse(ctx, i, players[i], now);
    }

    const seatByPlayerId = new Map<string, number>();
    for (let i = 0; i < players.length; i++) seatByPlayerId.set(players[i]!.playerId, i);

    // Per-frame decay factor for smoothing offsets. Exponential half-life:
    // factor = 0.5^(dt/halfLife) — works at any framerate.
    const smoothingDecay = Math.pow(0.5, dt / SMOOTH_HALF_LIFE_S);

    for (let i = 0; i < players.length; i++) {
      const player = players[i]!;
      for (const tower of player.towers) drawTower(ctx, tower, i, i);
      for (const unit of player.units) {
        const ownerSeat = seatByPlayerId.get(unit.ownerId) ?? i;
        // Forward extrapolation from the latest snapshot.
        let posX = unit.x + unit.vx * dtSinceSnapshot;
        let posY = unit.y + unit.vy * dtSinceSnapshot;
        // Apply + decay smoothing offset if any.
        const sm = unitSmoothing.get(unit.id);
        if (sm !== undefined) {
          sm.offsetX *= smoothingDecay;
          sm.offsetY *= smoothingDecay;
          if (Math.abs(sm.offsetX) < 0.001 && Math.abs(sm.offsetY) < 0.001) {
            unitSmoothing.delete(unit.id);
          } else {
            posX += sm.offsetX;
            posY += sm.offsetY;
          }
        }
        drawUnit(ctx, unit, i, posX, posY, ownerSeat);
      }
    }

    for (const proj of projectiles) {
      proj.progress = Math.min(1, proj.progress + dt / PROJECTILE_DURATION_S);
    }
    for (let i = projectiles.length - 1; i >= 0; i--) {
      if (projectiles[i]!.progress >= 1) projectiles.splice(i, 1);
    }
    for (const proj of projectiles) drawProjectile(ctx, proj);

    // Splash flashes first, so the in-flight shell renders on top of any
    // overlapping landing ring (looks cleaner when a wave is being shelled).
    for (let i = splashFlashes.length - 1; i >= 0; i--) {
      const f = splashFlashes[i]!;
      const ft = (now - f.startTime) / FLASH_DURATION_MS;
      if (ft >= 1) splashFlashes.splice(i, 1);
    }
    for (const f of splashFlashes) {
      const ft = Math.min(1, (now - f.startTime) / FLASH_DURATION_MS);
      drawSplashFlash(ctx, f, ft);
    }

    for (let i = splashProjectiles.length - 1; i >= 0; i--) {
      const p = splashProjectiles[i]!;
      if (now - p.startTime >= p.durationMs) splashProjectiles.splice(i, 1);
    }
    for (const p of splashProjectiles) {
      const t = clamp((now - p.startTime) / p.durationMs, 0, 1);
      drawSplashProjectile(ctx, p, t);
      if (showSplashRadii) {
        drawSplashRadiusOverlay(ctx, p.endX, p.endY, p.radiusPx, TOWER_COLORS[p.towerType] ?? "#ffffff");
      }
    }

    if (selectedCells.length > 0) {
      const cs = BASE_CELL_SIZE;
      ctx.fillStyle   = "rgba(255,214,102,0.18)";
      ctx.strokeStyle = "rgba(255,214,102,0.95)";
      ctx.lineWidth = 2;
      for (const sc of selectedCells) {
        const ox = laneX(sc.laneIndex);
        const x = ox + sc.col * cs;
        const y = sc.row * cs;
        ctx.fillRect(x, y, cs, cs);
        ctx.strokeRect(x + 1, y + 1, cs - 2, cs - 2);
      }
    }

    if (hoverCell !== null && hoverCell.laneIndex === ownLaneIndex) {
      drawHoverPreview(ctx, hoverCell, players[ownLaneIndex] ?? null);
    }

    debugOverlay.recordFrameTime(performance.now() - frameStart, now);
  }

  // Start centered on own lane at default zoom.
  focusOnLaneImpl(ownLaneIndex);

  rafId = requestAnimationFrame(frame);

  function focusOnLaneImpl(laneIndex: number): void {
    const laneStride = (LANE_COLS + GUTTER_COLS) * BASE_CELL_SIZE;
    camera.x = laneIndex * laneStride + (LANE_COLS * BASE_CELL_SIZE) / 2;
    camera.y = worldH / 2;
    camera.zoom = DEFAULT_ZOOM;
    clampCamera();
  }

  function ingestSplashShot(shot: SplashShot, now: number): void {
    const cs = BASE_CELL_SIZE;
    const ox = laneX(shot.laneIndex);
    const ticksRemaining = Math.max(1, shot.impactTick - shot.fireTick);
    splashProjectiles.push({
      startX: ox + shot.originCell.col * cs + cs / 2,
      startY: shot.originCell.row * cs + cs / 2,
      endX:   ox + shot.center.col * cs,
      endY:   shot.center.row * cs,
      towerType: shot.towerType,
      radiusPx: shot.radius * cs,
      startTime: now,
      durationMs: ticksRemaining * TICK_MS,
    });
  }

  function ingestSplashImpact(impact: SplashImpact, now: number): void {
    const cs = BASE_CELL_SIZE;
    const ox = laneX(impact.laneIndex);
    splashFlashes.push({
      worldX: ox + impact.center.col * cs,
      worldY: impact.center.row * cs,
      radiusPx: impact.radius * cs,
      towerType: impact.towerType,
      startTime: now,
    });
  }

  return {
    applySnapshot(snapshot: SnapshotMessage): void {
      const arrivedAt = performance.now();

      // Reconciliation step: for every unit that exists in both the OLD and
      // NEW snapshots, compute how far our forward-velocity prediction (from
      // the OLD snapshot) diverged from the NEW snapshot's truth. Push that
      // delta onto the unit's smoothing offset so the next frame's render
      // sees the SAME position as before this snapshot arrival — then decay
      // the offset over ~100ms to glide onto the new truth. Also reports the
      // average snap magnitude to the debug overlay (input to the smoother).
      if (lastSnapshot !== null && lastSnapshotTime > 0) {
        const dtSec = (arrivedAt - lastSnapshotTime) / 1000;
        const oldUnits = new Map<string, UnitSnapshot>();
        for (const p of lastSnapshot.players) for (const u of p.units) oldUnits.set(u.id, u);
        const liveUnitIds = new Set<string>();
        let sum = 0;
        let count = 0;
        for (const p of snapshot.players) for (const u of p.units) {
          liveUnitIds.add(u.id);
          const old = oldUnits.get(u.id);
          if (old === undefined) continue;
          // Where forward-extrapolation said the unit would be at arrival.
          const predX = old.x + old.vx * dtSec;
          const predY = old.y + old.vy * dtSec;
          // Snap delta = predicted MINUS actual. Adding this to the offset
          // means "render where we WERE rendering" right now; decay then
          // brings the offset toward zero across subsequent frames.
          const dx = predX - u.x;
          const dy = predY - u.y;
          // Diagnostic: report the magnitude (always positive) to the overlay.
          sum += Math.sqrt(dx * dx + dy * dy);
          count += 1;
          // Apply to the smoothing offset, capped. Anything beyond the cap
          // is too big to glide invisibly — snap instead (zero the offset).
          const existing = unitSmoothing.get(u.id);
          const newOx = (existing?.offsetX ?? 0) + dx;
          const newOy = (existing?.offsetY ?? 0) + dy;
          if (Math.sqrt(newOx * newOx + newOy * newOy) > SMOOTH_MAX_OFFSET_CELLS) {
            unitSmoothing.delete(u.id);
          } else if (existing !== undefined) {
            existing.offsetX = newOx;
            existing.offsetY = newOy;
          } else {
            unitSmoothing.set(u.id, { offsetX: newOx, offsetY: newOy });
          }
        }
        if (count > 0) debugOverlay.recordPredictionError(sum / count);
        // Drop smoothing entries for units that no longer exist (died/leaked).
        for (const id of unitSmoothing.keys()) {
          if (!liveUnitIds.has(id)) unitSmoothing.delete(id);
        }
      }

      lastSnapshot = snapshot;
      lastSnapshotTime = arrivedAt;

      // If any player's Lane Expansion level changed, rebuild the background
      // texture (lane geometry is baked in) and recompute world height for
      // camera bounds. Cheap — runs at most once per research click.
      let levelsChanged = false;
      for (let i = 0; i < snapshot.players.length && i < playerCount; i++) {
        const lvl = snapshot.players[i]!.laneExpansionLevel ?? 0;
        if (lvl !== laneLevels[i]) {
          laneLevels[i] = lvl;
          levelsChanged = true;
        }
      }
      if (levelsChanged) {
        bgTexture = buildBackgroundTexture(playerCount, laneLevels);
        worldH = maxLaneRows() * BASE_CELL_SIZE;
        clampCamera();
      }

      const cs = BASE_CELL_SIZE;
      for (const attack of snapshot.attacks) {
        const ox = laneX(attack.laneIndex);
        projectiles.push({
          startX: ox + attack.towerCell.col * cs + cs / 2,
          startY: attack.towerCell.row * cs + cs / 2,
          endX:   ox + attack.targetPos.col * cs,
          endY:   attack.targetPos.row * cs,
          towerType: attack.towerType,
          progress: 0,
        });
      }
      for (const shot of snapshot.splashShots) {
        ingestSplashShot(shot, arrivedAt);
      }
      for (const impact of snapshot.splashImpacts) {
        ingestSplashImpact(impact, arrivedAt);
      }
    },

    toggleSplashRadii(): boolean {
      showSplashRadii = !showSplashRadii;
      return showSplashRadii;
    },

    setHoverCell(cell: CellCoord | null): void { hoverCell = cell; },

    setSelectedCells(cells: CellCoord[]): void { selectedCells = cells; },

    resize(): void {
      applyCanvasSize();
      clampCamera();
    },

    cellFromClient(clientX: number, clientY: number): CellCoord | null {
      const w = clientToWorld(clientX, clientY);
      return cellFromWorld(w.x, w.y);
    },

    clientFromCell(laneIndex: number, col: number, row: number): { x: number; y: number } {
      const rect = canvas.getBoundingClientRect();
      const cs = BASE_CELL_SIZE;
      const laneStride = (LANE_COLS + GUTTER_COLS) * cs;
      const wx = laneIndex * laneStride + col * cs + cs / 2;
      const wy = row * cs + cs / 2;
      return {
        x: rect.left + rect.width  / 2 + (wx - camera.x) * camera.zoom,
        y: rect.top  + rect.height / 2 + (wy - camera.y) * camera.zoom,
      };
    },

    beginPan(clientX: number, clientY: number): void {
      const rect = canvas.getBoundingClientRect();
      panDrag = {
        startCssX: clientX - rect.left,
        startCssY: clientY - rect.top,
        startCamX: camera.x,
        startCamY: camera.y,
      };
    },

    updatePan(clientX: number, clientY: number): void {
      if (panDrag === null) return;
      const rect = canvas.getBoundingClientRect();
      const cssX = clientX - rect.left;
      const cssY = clientY - rect.top;
      const dxCss = cssX - panDrag.startCssX;
      const dyCss = cssY - panDrag.startCssY;
      camera.x = panDrag.startCamX - dxCss / camera.zoom;
      camera.y = panDrag.startCamY - dyCss / camera.zoom;
      clampCamera();
    },

    endPan(): void {
      panDrag = null;
    },

    panByClientPixels(dxCss: number, dyCss: number): void {
      camera.x += dxCss / camera.zoom;
      camera.y += dyCss / camera.zoom;
      clampCamera();
    },

    zoomAroundClient(clientX: number, clientY: number, factor: number): void {
      const before = clientToWorld(clientX, clientY);
      camera.zoom = clamp(camera.zoom * factor, MIN_ZOOM, MAX_ZOOM);
      const after = clientToWorld(clientX, clientY);
      camera.x += before.x - after.x;
      camera.y += before.y - after.y;
      clampCamera();
    },

    zoomAroundCenter(factor: number): void {
      camera.zoom = clamp(camera.zoom * factor, MIN_ZOOM, MAX_ZOOM);
      clampCamera();
    },

    focusOnLane(laneIndex: number): void {
      focusOnLaneImpl(laneIndex);
    },

    destroy(): void { cancelAnimationFrame(rafId); },
  };
}
